import dayjs from '~/timeInit';

import type { GridColDef, GridFilterModel } from '@mui/x-data-grid-pro';

// Leave room for the endpoint and other parameters within the gateway's URL limit.
const MAX_ENCODED_FILTER_QUERY_LENGTH = 7000;

export function toInventoryFilterDate(value: string): string | null {
  const date = dayjs(value.trim(), 'DD/MM/YYYY', true);
  return date.isValid() ? date.format('YYYY-MM-DD') : null;
}

// The grid supplies operators and input types; the API expects a JSON query parameter.
export function serializeInventoryFilters(model: GridFilterModel, columns: Array<GridColDef>): string | undefined {
  if (!model.items.length) return undefined;
  if (model.items.length > 20) throw new Error('Use at most 20 column filters.');

  const filters = model.items.map((item) => {
    const column = columns.find((candidate) => candidate.field === item.field);
    const operator = column?.filterOperators?.find((candidate) => candidate.value === item.operator);
    if (!operator) throw new Error('Choose a column and operator for every filter.');
    if (operator.requiresFilterValue === false) return { field: item.field, operator: item.operator };

    const numeric = operator.InputComponentProps?.type === 'number';
    const parseValue = (value: unknown) => {
      const text = String(value ?? '').trim();
      if (!text) throw new Error(`Enter a value for ${column.headerName}.`);
      if (item.field === 'depDate') {
        const date = toInventoryFilterDate(text);
        if (!date) throw new Error('Enter a valid departure date in DD/MM/YYYY.');
        return date;
      }
      if (numeric) {
        const number = Number(text);
        if (!Number.isFinite(number)) throw new Error(`Enter a valid number for ${column.headerName}.`);
        return number;
      }
      if (text.length > 200) throw new Error('Filter values must be at most 200 characters.');
      return text;
    };
    let value: string | number | Array<string | number>;
    if (item.operator === 'isAnyOf') {
      if (!Array.isArray(item.value) || !item.value.length || item.value.length > 20) {
        throw new Error(`Enter between 1 and 20 values for ${column.headerName}.`);
      }
      value = item.value.map(parseValue);
    } else {
      value = parseValue(item.value);
    }
    return { field: item.field, operator: item.operator, value };
  });
  const serialized = JSON.stringify(filters);
  if (serialized.length > 10000) throw new Error('The combined column filters are too long.');
  if (new URLSearchParams({ inventoryFilters: serialized }).toString().length > MAX_ENCODED_FILTER_QUERY_LENGTH) {
    throw new Error('The search filters are too long. Remove some filters or shorten their values.');
  }
  return serialized;
}
